// The GLOBAL closed-period refusal (board accounts/close--locked, HANDOFF
// §5.8, BRIEF hard rule 8): any write that erp-core refuses with 409
// `period_closed` — invoice edit, voucher post, manual entry, reversal dated
// in a closed month, import, stock move, payroll, bank statement — opens ONE
// dialog with two exits (a reversal dated today / a reopen request to the
// owner). Never a silent edit, never a generic error card.
//
// This module is React-free: a tiny store the shell's <LockedPeriodHost/>
// renders, plus the two ways an entry point reaches it:
//   1. automatically — the integrator puts `handlePeriodClosedMutationError`
//      in the QueryClient's MutationCache `onError`, so EVERY useMutation
//      that fails with period_closed opens the dialog (opt out per mutation
//      with `meta: { periodClosed: 'manual' }`, opt in to a document-level
//      reversal with `meta: { periodClosed: { reverse } }`);
//   2. explicitly — `showLockedPeriod(error, { reverse })` from any catch
//      (or the `usePeriodClosed()` hook).

import { isPeriodClosed, type ApiError } from '@/lib/api';
import type { PeriodClosedProblem } from './types';

/** The shell-level mapping lives in lib/api (isPeriodClosed / PERIOD_CLOSED); re-exported for callers here. */
export { isPeriodClosed, PERIOD_CLOSED } from '@/lib/api';

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** The problem's extension members, typed (missing booleans default to the server's `true`). */
export function periodClosedOf(error: unknown): PeriodClosedProblem | null {
  if (!isPeriodClosed(error)) return null;
  const x = error.extensions;
  return {
    document_type: str(x.document_type),
    document_word_ar: str(x.document_word_ar),
    document_id: strOrNull(x.document_id),
    entry_id: strOrNull(x.entry_id),
    document_no_display: strOrNull(x.document_no_display),
    document_date: str(x.document_date),
    // erp-core sends the period as YYYY-MM-01 (journal) or YYYY-MM; the screens use YYYY-MM.
    period_month: str(x.period_month).replace(/^(\d{4}-\d{2})-\d{2}$/, '$1'),
    closed_at: str(x.closed_at),
    closed_by: str(x.closed_by),
    closed_by_name: str(x.closed_by_name),
    reversal_available: x.reversal_available !== false,
    reopen_request_available: x.reopen_request_available !== false,
  };
}

/** What the reversal exit resolved to: the new entry's display number when known. */
export interface ReversalOutcome {
  entry_no_display?: string | null;
}

export interface LockedPeriodOptions {
  /**
   * The entry point's own «قيد عكس بتاريخ اليوم» (e.g. POST
   * /v1/erp/invoices/{id}/reverse). When omitted the dialog reverses through
   * the journal only when the problem names a journal entry
   * (`document_type` reversal | journal_entry with a `document_id`);
   * otherwise the reversal exit is absent (rule 6) and only the reopen
   * request remains.
   */
  reverse?: () => Promise<ReversalOutcome | unknown>;
  /** Called after a successful reversal (refresh the caller's document). */
  onReversed?: (outcome: ReversalOutcome | unknown) => void;
  /**
   * For a NEW (unposted) document there is nothing to reverse: the entry
   * point's own exit «date it today» (e.g. a receipt or a plan's first due
   * date moved to today and resubmitted). Shown as the coral only when no
   * reversal exit exists; the dialog closes, then `run()` is called.
   */
  redate?: { label: string; run: () => void };
}

export interface LockedPeriodRequest extends LockedPeriodOptions {
  id: number;
  error: ApiError;
  problem: PeriodClosedProblem;
}

type Listener = () => void;
let current: LockedPeriodRequest | null = null;
let seq = 0;
const listeners = new Set<Listener>();
const hosts: number[] = [];
let hostSeq = 0;

function emit() {
  listeners.forEach((l) => l());
}

export const lockedPeriodStore = {
  get: (): LockedPeriodRequest | null => current,
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Show the dialog for `error`. Returns false (and does nothing) for any other error. */
  show(error: unknown, opts: LockedPeriodOptions = {}): boolean {
    const problem = periodClosedOf(error);
    if (!problem) return false;
    current = { id: ++seq, error: error as ApiError, problem, ...opts };
    emit();
    return true;
  },
  close(): void {
    if (!current) return;
    current = null;
    emit();
  },
  /**
   * Host registry: several hosts may be mounted (the shell's, plus a
   * screen's fallback before the shell wiring lands); only the FIRST
   * registered one renders, so the dialog never appears twice.
   */
  registerHost(): number {
    const id = ++hostSeq;
    hosts.push(id);
    emit();
    return id;
  },
  unregisterHost(id: number): void {
    const i = hosts.indexOf(id);
    if (i >= 0) hosts.splice(i, 1);
    emit();
  },
  activeHost: (): number | null => hosts[0] ?? null,
  /** Test hook. */
  reset(): void {
    current = null;
    hosts.length = 0;
    emit();
  },
};

/** Explicit entry point: `catch (e) { if (showLockedPeriod(e, { reverse })) return; … }`. */
export function showLockedPeriod(error: unknown, opts?: LockedPeriodOptions): boolean {
  return lockedPeriodStore.show(error, opts);
}

/** `meta.periodClosed` on a mutation: 'manual' = the caller handles it; an object = options. */
export type PeriodClosedMeta = 'manual' | LockedPeriodOptions | undefined;

/**
 * For the QueryClient's MutationCache (integrator wiring):
 *   new MutationCache({ onError: handlePeriodClosedMutationError })
 * Fires the dialog for every mutation refused with period_closed unless the
 * mutation opted out with `meta: { periodClosed: 'manual' }`.
 */
export function handlePeriodClosedMutationError(
  error: unknown,
  _variables?: unknown,
  _context?: unknown,
  mutation?: { meta?: Record<string, unknown> },
): void {
  const meta = mutation?.meta?.periodClosed as PeriodClosedMeta;
  if (meta === 'manual') return;
  lockedPeriodStore.show(error, typeof meta === 'object' && meta ? meta : undefined);
}

/** Journal document types the dialog can reverse by itself (document_id = the entry to reverse). */
const JOURNAL_REVERSIBLE = new Set(['reversal', 'journal_entry']);

/**
 * The journal entry «قيد عكس بتاريخ اليوم» reverses: the problem's `entry_id`
 * when erp-core names it (any document type), else `document_id` for the
 * journal's own types. Otherwise null — the coral is absent unless the entry
 * point passes its own document-level reverse.
 */
export function journalEntryToReverse(p: PeriodClosedProblem): string | null {
  if (p.entry_id) return p.entry_id;
  return JOURNAL_REVERSIBLE.has(p.document_type) && p.document_id ? p.document_id : null;
}
